import { useEffect, useRef, useCallback, useState } from 'react';
import { IS_APP_BUILD } from '../lib/buildTarget';
// smoke-alumno (S11): la huella cierra el contexto de audio que FingerprintJS deja vivo en Safari.
import { obtenerHuella } from '../lib/huellaSinAudio';

const TURNSTILE_SITE_KEY = '0x4AAAAAAColuOgzAnOVo6ZZ';

// WHY Turnstile is gated off in APP builds (F4.2c): inside the Capacitor shell
// the WebView origin is capacitor://localhost, which the site key can never
// validate as an allowed hostname — the widget cannot mint a token there, so
// getTurnstileToken() would just burn its full 8s timeout on every guest
// signup/upload before resolving ''. The backend is fail-open when no token
// arrives (backend/services/security.py — verified in the F4 census), so
// skipping the widget only removes the dead wait; effective security is
// unchanged. In the web build IS_APP_BUILD is false and behavior is identical.

/**
 * Hook that provides Turnstile token generation and device fingerprinting.
 */
export function useSecurity() {
  const [fingerprint, setFingerprint] = useState('');
  const fpInstanceRef = useRef(null);
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);
  const tokenRef = useRef('');
  const resolveRef = useRef(null);

  // Initialize FingerprintJS (lazy load)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
        const fp = await FingerprintJS.load();
        fpInstanceRef.current = fp;
        const result = await obtenerHuella(fp);
        if (!cancelled) setFingerprint(result.visitorId);
      } catch (e) {
        console.warn('FingerprintJS init failed:', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // On-demand fingerprint getter with retry (for upload time)
  const getFingerprint = useCallback(async () => {
    // Already have it
    if (fingerprint) return fingerprint;

    // Retry with existing instance
    if (fpInstanceRef.current) {
      try {
        const result = await obtenerHuella(fpInstanceRef.current);
        const vid = result.visitorId;
        if (vid) { setFingerprint(vid); return vid; }
      } catch (e) {
        console.warn('FingerprintJS retry failed:', e);
      }
    }

    // Full reload attempt
    try {
      const FingerprintJS = (await import('@fingerprintjs/fingerprintjs')).default;
      const fp = await FingerprintJS.load();
      fpInstanceRef.current = fp;
      const result = await obtenerHuella(fp);
      const vid = result.visitorId;
      if (vid) { setFingerprint(vid); return vid; }
    } catch (e) {
      console.warn('FingerprintJS reload failed:', e);
    }

    return '';
  }, [fingerprint]);

  // Render invisible Turnstile widget (web only — see the gate note above).
  useEffect(() => {
    if (IS_APP_BUILD) return;
    const interval = setInterval(() => {
      try {
        if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: TURNSTILE_SITE_KEY,
            size: 'compact',
            appearance: 'interaction-only',
            execution: 'render',
            callback: (token) => {
              tokenRef.current = token;
              if (resolveRef.current) {
                resolveRef.current(token);
                resolveRef.current = null;
              }
            },
            'error-callback': () => {
              tokenRef.current = '';
              if (resolveRef.current) {
                resolveRef.current('');
                resolveRef.current = null;
              }
            },
          });
          clearInterval(interval);
        }
      } catch (e) {
        console.warn('Turnstile render failed:', e);
      }
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const getTurnstileToken = useCallback(() => {
    return new Promise((resolve) => {
      // App build: no widget was rendered — resolve the empty token immediately
      // instead of waiting out the 8s timeout (call-sites already tolerate '').
      if (IS_APP_BUILD) {
        resolve('');
        return;
      }
      if (!window.turnstile || widgetIdRef.current === null) {
        resolve('');
        return;
      }
      if (tokenRef.current) {
        const token = tokenRef.current;
        tokenRef.current = '';
        try { window.turnstile.reset(widgetIdRef.current); } catch (e) {}
        resolve(token);
        return;
      }
      resolveRef.current = resolve;
      try { window.turnstile.reset(widgetIdRef.current); } catch (e) {}
      setTimeout(() => {
        if (resolveRef.current) {
          resolveRef.current('');
          resolveRef.current = null;
        }
      }, 8000);
    });
  }, []);

  return { fingerprint, getFingerprint, getTurnstileToken, turnstileRef };
}
